import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSiteContent } from '../hooks/useSiteContent';
import EmberField from './EmberField';
import BioSheet from './BioSheet';
import { ARACELY_BIO, AMY_BIO } from '../data/heroBios';

/**
 * RotatingHeroWord — cycles through the supplied phrases every 4 s,
 * using a 3D billboard/scoreboard flip on the X-axis: each phrase
 * enters from below (rotated -90°), settles flat, then flips out over
 * the top (rotated +90°). `prefers-reduced-motion` and pause-on-tab-
 * hidden are both honoured so the effect feels premium without ever
 * wasting cycles.
 */
function RotatingHeroWord({ words, interval = 4000 }) {
  const [idx, setIdx] = useState(0);
  // Honour the user's reduced-motion preference — static word, no cycling.
  const [reduced] = useState(() =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    if (reduced) return undefined;
    const id = setInterval(() => {
      // Pause the cycle while the tab is in the background — saves CPU.
      if (document.visibilityState !== 'visible') return;
      setIdx((i) => (i + 1) % words.length);
    }, interval);
    return () => clearInterval(id);
  }, [words.length, interval, reduced]);

  if (reduced) {
    return <span className="inline-block">{words[0]}</span>;
  }

  return (
    <span
      className="relative inline-block align-baseline"
      style={{
        // Tall enough to hold the largest line-height without clipping.
        minHeight: '1em',
        perspective: '1100px',
      }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={words[idx]}
          /* IMPORTANT: the parent span uses `text-gradient-red` which
             sets `-webkit-text-fill-color: transparent`. That CANNOT
             be inherited through an `inline-block` child — so we apply
             the red gradient directly on this motion.span. */
          className="inline-block text-gradient-red"
          style={{ transformOrigin: '50% 50%', transformStyle: 'preserve-3d' }}
          initial={{ opacity: 0, y: 30, rotateX: -90 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          exit={{ opacity: 0, y: -30, rotateX: 90 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {words[idx]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// Hero team images
const ARACELY_URL = '/img/aracely-2026-07.webp';
const AMY_URL = '/img/df2ou4wz_HE2A5111copy.webp';

// Particles canvas — drifting embers (perf-optimized)
//
// SAFARI MOBILE NOTE: On iOS Safari the per-tab memory budget is small
// (~256 MB) and a long-running canvas RAF loop adds steady GPU/paint
// pressure even when the hero scrolls out of view. We:
//   (a) skip the canvas entirely on coarse-pointer devices (the global
//       EmberField already provides ambient particle motion), and
//   (b) pause the loop via IntersectionObserver when the hero section
//       leaves the viewport on desktop (was only paused on tab-hidden).
function Particles() {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    // Skip on touch / iOS Safari — EmberField provides the visual.
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d', { alpha: true });
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    const setSize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setSize();
    const onResize = () => setSize();
    window.addEventListener('resize', onResize, { passive: true });
    const N = 32;
    const particles = Array.from({ length: N }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.4 + 0.5,
      vy: -(Math.random() * 0.4 + 0.15),
      vx: (Math.random() - 0.5) * 0.2,
      a: Math.random() * 0.5 + 0.25,
    }));
    let raf;
    let docVisible = !document.hidden;
    let inViewport = true;
    const onVis = () => { docVisible = !document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    // IO pause — stop the RAF entirely when hero is off-screen.
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([e]) => { inViewport = e.isIntersecting; }, { threshold: 0 });
      io.observe(canvas);
    }
    const tick = () => {
      if (!docVisible || !inViewport) { raf = requestAnimationFrame(tick); return; }
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -10) { p.y = h + 10; p.x = Math.random() * w; }
        if (p.x < -10) p.x = w + 10;
        else if (p.x > w + 10) p.x = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(239, 43, 43, ${p.a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVis);
      if (io) io.disconnect();
    };
  }, []);
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}

function GlitchTitle({ children, className }) {
  return (
    <span className={`relative inline-block ${className}`}>
      {children}
      <span aria-hidden className="absolute inset-0 text-red-500/20 translate-x-[3px] translate-y-[3px] pointer-events-none select-none">{children}</span>
    </span>
  );
}

// Auto-typing classes ticker
function ClassTicker() {
  const { content } = useSiteContent();
  const items = useMemo(() => {
    const titles = [];
    (content.classPrograms || []).forEach((p) => p.plans.forEach((pl) => titles.push(pl.name)));
    return titles.length ? titles : ['Boot Camp', 'Yoga', 'Spin', 'Pilates', 'Zumba'];
  }, [content.classPrograms]);
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState('');
  const [phase, setPhase] = useState('typing');

  useEffect(() => {
    const target = items[idx];
    let t;
    if (phase === 'typing') {
      if (text.length < target.length) {
        t = setTimeout(() => setText(target.slice(0, text.length + 1)), 60);
      } else {
        t = setTimeout(() => setPhase('deleting'), 1400);
      }
    } else if (phase === 'deleting') {
      if (text.length > 0) {
        t = setTimeout(() => setText(target.slice(0, text.length - 1)), 30);
      } else {
        setPhase('typing');
        setIdx((i) => (i + 1) % items.length);
      }
    }
    return () => clearTimeout(t);
  }, [text, phase, idx, items]);

  return (
    <span className="inline-flex items-baseline">
      <span className="text-gradient-red">{text}</span>
      <span className="ml-1 inline-block w-[3px] h-[0.85em] bg-red-500 animate-pulse" />
    </span>
  );
}

// Cinematic team stage — Aracely + Amy (EQUAL SIZE, sequential focus reveal)
// Animation sequence:
//   phase 0 → both hidden (pre-mount)
//   phase 1 → Aracely focuses in (scaled up, brighter)
//   phase 2 → Aracely settles, Amy focuses in (scaled up, brighter)
//   phase 3 → Both settled, equal size, still side-by-side
function TeamStage({ onOpenBio }) {
  const [phase, setPhase] = useState(0);
  const stageRef = useRef(null);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 200);   // Aracely enters & focuses
    const t2 = setTimeout(() => setPhase(2), 1500);  // Aracely → still, Amy focuses
    const t3 = setTimeout(() => setPhase(3), 2800);  // Both settle equal & still
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  // Subtle parallax on mouse move
  useEffect(() => {
    if (typeof window === 'undefined' || window.matchMedia('(pointer: coarse)').matches) return;
    let raf;
    let lastE = null;
    const onMove = (e) => {
      lastE = e;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const rect = stageRef.current?.getBoundingClientRect();
        if (!rect || !lastE) return;
        const x = (lastE.clientX - rect.left) / rect.width - 0.5;
        const y = (lastE.clientY - rect.top) / rect.height - 0.5;
        stageRef.current.style.setProperty('--px', `${x * 12}px`);
        stageRef.current.style.setProperty('--py', `${y * 8}px`);
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => { window.removeEventListener('mousemove', onMove); cancelAnimationFrame(raf); };
  }, []);

  return (
    <div ref={stageRef} className="team-stage relative w-full h-full">
      {/* Cinematic spotlight beam from top */}
      <div className="hero-mobile-decor-hide absolute inset-0 flex items-start justify-center pointer-events-none overflow-hidden">
        <div
          className="w-[55%] h-full opacity-70"
          style={{
            background: 'linear-gradient(180deg, rgba(239,43,43,0.30) 0%, rgba(239,43,43,0.06) 55%, transparent 100%)',
            clipPath: 'polygon(40% 0%, 60% 0%, 88% 100%, 12% 100%)',
            filter: 'blur(24px)',
          }}
        />
      </div>

      {/* Soft red ambient glow halo */}
      <div className="hero-mobile-decor-hide absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[520px] h-[520px] rounded-full bg-red-600/22 blur-[100px] animate-glow" />
      </div>

      {/* Team-stage field — denser cluster behind the trainers for
          depth. Fewer gym icons here so they don't compete with the
          photo cards. Non-reactive (single global listener is enough). */}
      <EmberField count={80} flames={12} gym={6} reactive={false} />

      {/* Concentric floor rings */}
      <div className="hero-mobile-decor-hide absolute pointer-events-none" style={{ left: '50%', bottom: '4%', transform: 'translateX(-50%) perspective(1400px) rotateX(72deg)' }}>
        <div
          className="w-[660px] h-[660px] rounded-full border border-red-600/35"
          style={{ animation: 'spin 26s linear infinite', boxShadow: '0 0 60px rgba(239,43,43,0.18)' }}
        />
        <div
          className="absolute inset-12 rounded-full border-2 border-dashed border-red-600/22"
          style={{ animation: 'spin 36s linear infinite reverse' }}
        />
        <div
          className="absolute inset-32 rounded-full border border-red-600/15"
          style={{ animation: 'spin 22s linear infinite' }}
        />
      </div>

      {/* Crosshair markers */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-red-600/15 to-transparent" />
      </div>

      {/* === ARACELY — EQUAL SIZE, focuses in first === */}
      <button
        type="button"
        onClick={() => onOpenBio && onOpenBio(ARACELY_BIO)}
        data-testid="hero-aracely-bio-cta"
        aria-label="Tap for Aracely Garcia bio"
        className={`team-aracely absolute top-[2%] left-[2%] sm:left-[3%] w-[46%] sm:w-[45%] aspect-[9/16] transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] block p-0 m-0 bg-transparent border-0 text-left cursor-pointer
          ${phase === 0 ? 'opacity-0' : 'opacity-100'}
          ${phase === 1 ? 'z-20' : 'z-10'}`}
        style={{
          transform: `translate3d(var(--px,0px), ${phase === 0 ? '40px' : 'var(--py,0px)'}, 0) scale(${phase === 0 ? 0.9 : phase === 1 ? 1.06 : 1})`,
        }}
      >
        {/* Clean photo frame — focus ring brightens on phase 1 */}
        <div
          className={`relative w-full h-full rounded-[24px] overflow-hidden border-[3px] bg-zinc-900 transition-all duration-700
            ${phase === 1
              ? 'border-red-500 shadow-[0_30px_90px_rgba(239,43,43,0.6),0_0_0_2px_rgba(239,43,43,0.5)]'
              : 'border-red-600/55 shadow-[0_24px_60px_rgba(239,43,43,0.3),0_0_0_1px_rgba(255,255,255,0.06)]'}`}
        >
          <img
            src={ARACELY_URL}
            alt="Aracely Garcia, Team Leader"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${phase === 2 ? 'brightness-75 saturate-75' : 'brightness-100'}`}
            style={{ objectPosition: 'center 18%' }}
          />
          {/* Top gradient to keep the badge legible without covering her face */}
          <div className="absolute inset-x-0 top-0 h-[18%] bg-gradient-to-b from-black/65 to-transparent pointer-events-none" />
          {/* Inside-card title badge — sits at top of photo, centered, never overlaps stats above */}
          <div className="absolute top-2.5 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 z-20 flex justify-center pointer-events-none">
            <div className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-red-600 text-white text-[8.5px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.32em] font-bold shadow-[0_6px_20px_rgba(239,43,43,0.55)] whitespace-nowrap">
              ★ TEAM LEADER
            </div>
          </div>
          {/* Glint sweep */}
          <div className="aracely-glint absolute inset-0 pointer-events-none" />
          {/* Focus pulse ring — only visible in phase 1 */}
          {phase === 1 && (
            <div className="absolute inset-0 rounded-[24px] pointer-events-none animate-focus-pulse" />
          )}

          {/* TAP FOR BIO pill — sits at bottom of photo, semi-transparent
              so it doesn't fight the portrait. Same lightweight CSS
              (gradient + opacity) used on the trainer-grid cards. */}
          <div className="absolute inset-x-0 bottom-0 h-[28%] bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
          <div className="absolute bottom-2.5 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 z-20 flex justify-center pointer-events-none">
            <div className="px-2 sm:px-2.5 py-1 rounded-full bg-red-600 text-white text-[8px] sm:text-[9px] tracking-[0.22em] sm:tracking-[0.3em] font-bold shadow-[0_4px_14px_rgba(239,43,43,0.55)] whitespace-nowrap">
              TAP FOR BIO
            </div>
          </div>
        </div>

        {/* Below-image caption plate — clean, tight, anchored to card width */}
        <div className="absolute -bottom-[52px] sm:-bottom-14 left-0 right-0 px-1 z-10 text-left">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[7.5px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.4em] text-red-400 font-bold mb-1">
            <div className="h-[1.5px] w-4 sm:w-6 bg-red-500 shrink-0" />
            <span className="truncate">TEAM LEADER · GALT, CA</span>
          </div>
          <div className="font-display text-base sm:text-2xl md:text-[28px] text-white leading-none truncate">
            ARACELY <span className="text-red-500">GARCIA</span>
          </div>
        </div>
      </button>

      {/* === AMY — EQUAL SIZE, focuses in second === */}
      <button
        type="button"
        onClick={() => onOpenBio && onOpenBio(AMY_BIO)}
        data-testid="hero-amy-bio-cta"
        aria-label="Tap for Amy Farris bio"
        className={`team-amy absolute top-[2%] right-[2%] sm:right-[3%] w-[46%] sm:w-[45%] aspect-[9/16] transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] block p-0 m-0 bg-transparent border-0 text-left cursor-pointer
          ${phase < 2 ? 'opacity-0' : 'opacity-100'}
          ${phase === 2 ? 'z-20' : 'z-10'}`}
        style={{
          transform: `translate3d(calc(var(--px,0px) * -0.6), ${phase < 2 ? '40px' : 'calc(var(--py,0px) * 0.6)'}, 0) scale(${phase < 2 ? 0.9 : phase === 2 ? 1.06 : 1})`,
        }}
      >
        {/* Clean photo frame — focus ring brightens on phase 2 */}
        <div
          className={`relative w-full h-full rounded-[24px] overflow-hidden border-[3px] bg-zinc-900 transition-all duration-700
            ${phase === 2
              ? 'border-red-500 shadow-[0_30px_90px_rgba(239,43,43,0.6),0_0_0_2px_rgba(239,43,43,0.5)]'
              : 'border-red-600/55 shadow-[0_24px_60px_rgba(239,43,43,0.3),0_0_0_1px_rgba(255,255,255,0.06)]'}`}
        >
          <img
            src={AMY_URL}
            alt="Amy Farris, Fitness Director at FitnessPro Galt"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${phase === 1 ? 'brightness-75 saturate-75' : 'brightness-100'}`}
            style={{ objectPosition: 'center 18%' }}
          />
          {/* Top gradient to keep the badge legible without covering her face */}
          <div className="absolute inset-x-0 top-0 h-[18%] bg-gradient-to-b from-black/65 to-transparent pointer-events-none" />
          {/* Inside-card title badge — secondary treatment: outlined to keep Aracely visually dominant */}
          <div className="absolute top-2.5 left-2 right-2 sm:top-3 sm:left-3 sm:right-3 z-20 flex justify-center pointer-events-none">
            <div className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-black/70 backdrop-blur border border-red-500/70 text-white text-[8.5px] sm:text-[10px] tracking-[0.22em] sm:tracking-[0.32em] font-bold shadow-[0_4px_14px_rgba(0,0,0,0.45)] whitespace-nowrap">
              <span className="sm:hidden">★ DIRECTOR</span>
              <span className="hidden sm:inline">★ FITNESS DIRECTOR</span>
            </div>
          </div>
          {phase === 2 && (
            <div className="absolute inset-0 rounded-[24px] pointer-events-none animate-focus-pulse" />
          )}

          {/* TAP FOR BIO pill (Amy) */}
          <div className="absolute inset-x-0 bottom-0 h-[28%] bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
          <div className="absolute bottom-2.5 left-2 right-2 sm:bottom-3 sm:left-3 sm:right-3 z-20 flex justify-center pointer-events-none">
            <div className="px-2 sm:px-2.5 py-1 rounded-full bg-red-600 text-white text-[8px] sm:text-[9px] tracking-[0.22em] sm:tracking-[0.3em] font-bold shadow-[0_4px_14px_rgba(239,43,43,0.55)] whitespace-nowrap">
              TAP FOR BIO
            </div>
          </div>
        </div>

        {/* Below-image caption — IDENTICAL styling to Aracely's plate */}
        <div className="absolute -bottom-[52px] sm:-bottom-14 left-0 right-0 px-1 z-10 text-left">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[7.5px] sm:text-[9px] tracking-[0.25em] sm:tracking-[0.4em] text-red-400 font-bold mb-1">
            <div className="h-[1.5px] w-4 sm:w-6 bg-red-500 shrink-0" />
            <span className="truncate">YOGA THERAPY · 14 YRS</span>
          </div>
          <div className="font-display text-base sm:text-2xl md:text-[28px] text-white leading-none truncate">
            AMY <span className="text-red-500">FARRIS</span>
          </div>
        </div>
      </button>

      {/* Lens flare accent */}
      <div className="absolute top-[8%] left-1/2 -translate-x-1/2 w-40 h-1 bg-gradient-to-r from-transparent via-red-500/70 to-transparent blur-sm pointer-events-none" />
    </div>
  );
}

// Premium glassmorphism play CTA — animated rings, glow expansion, light sweep
function PremiumPlayCTA({ onClick }) {
  return (
    <button
      onClick={onClick}
      data-cursor="hover"
      data-testid="hero-play-cta"
      className="play-cta group inline-flex items-center gap-4 text-white"
      aria-label="Take the gym tour"
    >
      <span className="play-cta-orb relative w-16 h-16 rounded-full flex items-center justify-center">
        {/* Outer animated ring */}
        <span className="play-cta-ring absolute inset-0 rounded-full border border-red-500/50" />
        <span className="play-cta-ring-2 absolute inset-0 rounded-full border border-red-500/30" />
        {/* Glassmorphism core */}
        <span className="absolute inset-1.5 rounded-full bg-black/65 backdrop-blur-xl border border-white/15 group-hover:border-red-500/70 transition-colors" />
        {/* Light sweep on hover */}
        <span className="play-cta-sweep absolute inset-1.5 rounded-full overflow-hidden pointer-events-none">
          <span className="play-cta-sweep-bar absolute inset-0" />
        </span>
        {/* Core icon */}
        <Play className="relative w-5 h-5 ml-0.5 fill-white text-white group-hover:fill-red-500 group-hover:text-red-500 transition-colors" strokeWidth={1.5} />
        {/* Outer glow */}
        <span className="play-cta-glow absolute -inset-2 rounded-full bg-red-600/0 group-hover:bg-red-600/30 transition-all duration-500 blur-xl" />
      </span>
      <span className="flex flex-col items-start leading-none">
        <span className="text-[9px] tracking-[0.4em] text-red-400 font-bold">[ 60s ]</span>
        <span className="mt-1.5 text-xs tracking-[0.3em] font-bold">TAKE THE TOUR</span>
      </span>
    </button>
  );
}

export default function Hero({ onPlayVideo }) {
  const { content } = useSiteContent();
  const trainersCount = (content.trainers || []).length;
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const ctaRef = useRef(null);
  const [mounted, setMounted] = useState(false);

  // Bio modal state — same lightweight BioSheet component the trainer
  // grid + HeroDemoStrip already use. Single source of truth, single
  // mount point, no duplicate modals. `bioPerson` stays populated
  // through the close-out fade so the photo doesn't pop to black.
  const [bioOpen, setBioOpen] = useState(false);
  const [bioPerson, setBioPerson] = useState(null);
  const openBio = useCallback((person) => {
    if (!person) return;
    setBioPerson(person);
    setBioOpen(true);
  }, []);
  const handleSheetChange = useCallback((next) => {
    if (next) { setBioOpen(true); return; }
    setBioOpen(false);
    // Clear payload AFTER the BioSheet's 220 ms close-out animation.
    const t = setTimeout(() => setBioPerson(null), 260);
    return () => clearTimeout(t);
  }, []);

  // Scroll-driven parallax — translates a few decorative elements via a CSS
  // custom property on the hero <section>. Single rAF-throttled passive scroll
  // listener; auto-pauses when the hero is fully out of view (IntersectionObserver).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = heroRef.current;
    if (!el) return;

    let pending = false;
    const update = () => {
      pending = false;
      const y = window.scrollY || 0;
      // Cap the parallax — anything past 800px is full effect.
      const t = Math.min(1, Math.max(0, y / 800));
      el.style.setProperty('--hero-scroll', t.toFixed(3));
    };
    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  // Magnetic CTA button effect (rAF-batched, skipped on coarse pointers)
  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined' && (window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      return;
    }
    let pending = false;
    let lastE = null;
    const apply = () => {
      pending = false;
      const e = lastE;
      if (!e) return;
      const rect = heroRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (titleRef.current) {
        titleRef.current.style.transform = `translate3d(${x * -12}px, ${y * -8}px, 0)`;
      }
      if (ctaRef.current) {
        const btnRect = ctaRef.current.getBoundingClientRect();
        const dx = e.clientX - (btnRect.left + btnRect.width / 2);
        const dy = e.clientY - (btnRect.top + btnRect.height / 2);
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 180) {
          const force = (180 - dist) / 180;
          ctaRef.current.style.transform = `translate3d(${dx * 0.18 * force}px, ${dy * 0.18 * force}px, 0)`;
        } else {
          ctaRef.current.style.transform = 'translate3d(0,0,0)';
        }
      }
    };
    const onMove = (e) => {
      lastE = e;
      if (!pending) {
        pending = true;
        requestAnimationFrame(apply);
      }
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section id="home" ref={heroRef} className="relative min-h-[680px] sm:min-h-[760px] lg:min-h-screen overflow-hidden bg-black">
      {/* Animated grid */}
      <div className="absolute inset-0 grid-bg opacity-25" />
      {/* Wide-scope ember field — covers the entire hero. Includes
          embers + flames + tumbling gym equipment. Mouse-reactive.
          (Replaces the older canvas <Particles /> — CSS animation is
          GPU-composited and cheaper per frame than canvas re-paint.) */}
      <EmberField count={70} flames={20} gym={14} reactive />
      {/* Radial accents — drift up subtly on scroll (wrapper) while inner keeps its float anim */}
      <div
        className="hero-mobile-decor-hide absolute top-[15%] right-[10%] w-72 h-72"
        style={{ transform: 'translate3d(0, calc(var(--hero-scroll, 0) * -160px), 0)', willChange: 'transform' }}
      >
        <div className="w-full h-full rounded-full bg-red-600/15 blur-3xl animate-float-slow" />
      </div>
      <div
        className="hero-mobile-decor-hide absolute bottom-[15%] left-[5%] w-80 h-80"
        style={{ transform: 'translate3d(0, calc(var(--hero-scroll, 0) * 120px), 0)', willChange: 'transform' }}
      >
        <div className="w-full h-full rounded-full bg-red-600/8 blur-3xl animate-float-fast" />
      </div>
      {/* Soft vignette */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black pointer-events-none" />

      {/* Vertical side text */}
      <div className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-6">
        <div className="text-[10px] tracking-[0.5em] text-zinc-500" style={{ writingMode: 'vertical-rl' }}>
          KNOW PAIN · KNOW GAIN
        </div>
        <div className="w-[1px] h-32 bg-gradient-to-b from-red-600 to-transparent" />
      </div>
      <div className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 flex-col items-center gap-6">
        <div className="w-[1px] h-32 bg-gradient-to-t from-red-600 to-transparent" />
        <div className="text-[10px] tracking-[0.5em] text-zinc-500" style={{ writingMode: 'vertical-rl' }}>
          24 / 7 ACCESS · GALT, CA
        </div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-12 sm:pt-28 sm:pb-20 min-h-[600px] sm:min-h-[700px] lg:min-h-screen grid lg:grid-cols-12 gap-6 sm:gap-8 items-center">
        {/* LEFT — copy */}
        <div className="lg:col-span-6 lg:pr-6">
          <div className={`flex items-center gap-3 mb-7 transition-all duration-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="h-[2px] w-12 bg-red-600" />
            <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">EST. 1992 · 34 YEARS STRONG</span>
          </div>

          <h1 ref={titleRef} className="font-display text-[clamp(3rem,8.5vw,7.5rem)] leading-[0.85] tracking-tight transition-transform duration-200 ease-out">
            <span className={`block transition-all duration-1000 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <GlitchTitle>INVEST IN</GlitchTitle>
            </span>
            <span className={`block transition-all duration-1000 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <RotatingHeroWord words={['YOUR HEALTH', 'YOUR BODY', 'YOUR LIFE', 'YOUR FUTURE']} interval={4000} />
            </span>
          </h1>

          <div className={`mt-5 flex items-center gap-3 text-base md:text-lg text-zinc-300 transition-all duration-1000 delay-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="text-zinc-500">Now starting:</span>
            <ClassTicker />
          </div>

          <p className={`mt-7 max-w-xl text-zinc-400 text-base leading-relaxed transition-all duration-1000 delay-600 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            A passionate, determined, and supportive fitness community in Galt, CA. <span className="text-white">17,000 sq ft</span> of premium equipment, <span className="text-white">24/7 access</span>, certified coaches who actually care.
          </p>

          <div className={`mt-10 flex flex-wrap items-center gap-6 transition-all duration-1000 delay-700 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <a
              ref={ctaRef}
              href="#membership-enrollment"
              data-cursor="hover"
              data-testid="hero-membership-cta"
              className="btn-shimmer group relative inline-flex items-center gap-3 px-8 py-4 bg-red-600 hover:bg-red-500 text-white text-sm tracking-[0.2em] font-bold rounded-md transition-colors will-change-transform"
              style={{ transition: 'transform 0.2s ease-out, background-color 0.2s ease' }}
            >
              VIEW MEMBERSHIPS
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <PremiumPlayCTA onClick={onPlayVideo} />
          </div>

          <div className={`mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl transition-all duration-1000 delay-1000 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {[
              { v: '34+', l: 'Years' },
              { v: '17K', l: 'Sq Ft' },
              { v: '24/7', l: 'Access' },
              { v: trainersCount.toString(), l: 'Coaches' },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-red-600/50 pl-4 hover:border-red-500 transition group cursor-default">
                <div className="font-display text-3xl md:text-4xl text-white group-hover:text-red-500 transition">{s.v}</div>
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Cinematic team stage */}
        <div
          className={`lg:col-span-6 relative h-[420px] sm:h-[520px] lg:h-[640px] transition-all duration-1000 delay-300 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
          style={{
            transform: 'translate3d(0, calc(var(--hero-scroll, 0) * -80px), 0)',
            willChange: 'transform',
          }}
        >
          <TeamStage onOpenBio={openBio} />
        </div>
      </div>

      {/* Bio modal — reuses the trainer-grid / HeroDemoStrip BioSheet so
          there's a single battle-tested implementation handling iOS
          scroll-lock, browser-back, dvh sizing, and image fade-in. */}
      <BioSheet open={bioOpen} onOpenChange={handleSheetChange} person={bioPerson} />
    </section>
  );
}
